import { useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import { listAddons, updateAddon } from "../../api/superAdminService";
import { showApiError, showToast } from "../../utils/toast";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function AddonsPage() {
  const addons = useApi(() => listAddons());
  const [editing, setEditing] = useState(null);
  const rows = addons.data?.data || [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Addons</h1>
          <div className="page-sub">Catalog pricing — enable per firm from the firm page. New price applies at each firm's next renewal snapshot.</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {addons.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={3} /></div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Addon</th>
                  <th className="t-num">Monthly price</th>
                  <th className="t-num">Yearly (billed)</th>
                  <th className="t-num">Active firms</th>
                  <th>Status</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="t-primary">{a.name}</div>
                      <div className="t-muted text-xs">{a.description}</div>
                    </td>
                    <td className="t-num">{inr(a.monthly_price)}{a.unit_label ? `/${a.unit_label}` : ""}</td>
                    <td className="t-num">{inr(a.monthly_price * 12)}{a.unit_label ? `/${a.unit_label}` : ""}</td>
                    <td className="t-num">{a.active_firms_count}</td>
                    <td>
                      {a.is_active
                        ? <span className="badge badge-success">In catalog</span>
                        : <span className="badge badge-neutral">Hidden</span>}
                    </td>
                    <td>
                      <button className="icon-btn" title="Edit" onClick={() => setEditing(a)}>
                        <Icon name="edit" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Keyed by addon id so the form state resets per row. */}
      {editing && (
        <EditAddonDrawer
          key={editing.id}
          addon={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); addons.reload(); }}
        />
      )}
    </>
  );
}

function EditAddonDrawer({ addon, onClose, onSaved }) {
  const [price, setPrice] = useState(String(addon.monthly_price ?? ""));
  const [isActive, setIsActive] = useState(!!addon.is_active);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await updateAddon(addon.id, { monthly_price: Number(price), is_active: isActive });
      showToast("success", "Addon updated");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Edit ${addon.name}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || price === ""}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Monthly price (₹)</label>
        <input className="input" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
        <div className="field-hint">Billed yearly (×12) as part of each firm's renewal snapshot.</div>
      </div>
      <div className="field">
        <label className="row gap-sm" style={{ cursor: "pointer" }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Available in the catalog
        </label>
      </div>
    </Drawer>
  );
}
